import { supabaseClient } from '@/configs/supabase'
import { Row } from '@/libs/supabase/supabase.interface'
import { TOrderForm } from '@/components/OrderForm/OrderForm.interface'
import {
  IGetOrderBookHistoryFilter,
  ICreateOrderBookInput,
  IUpdateOrderBookInput,
  PaymentStatus,
  OrderBookStatus
} from './orderbook.interface'
import {
  ICustomerPricing,
  ICustomerWithPricing
} from '@/supabase/Customer/customer.interface'
import {
  getCustomerById,
  getDefaultPricing,
  getDefaultCustomerPricing,
  checkOrCreateCustomer,
  ensureDefaultCustomer
} from '@/supabase/Customer/customer'
import {
  calculateOrderTotal,
  calculateOutstandingBalance,
  extractPricingFromCustomerPricing
} from '@/utils/orderCalculations'

const getCurrentTimestamp = () => new Date()

const handleError = (error: any, message: string) => {
  console.error(message, error)
  return null
}

const getCustomerPricing = async (
  customerId: string
): Promise<ICustomerPricing | null> => {
  // First try to get customer with their specific pricing
  const customer = await getCustomerById(customerId)

  if (customer?.pricing) {
    return customer.pricing
  }

  // If customer not found or no pricing, get default customer pricing (id = 'default')
  const defaultCustomerPricing = await getDefaultCustomerPricing()

  if (defaultCustomerPricing) {
    return defaultCustomerPricing
  }

  // Fallback to the original getDefaultPricing if no default customer found
  const defaultPricing = await getDefaultPricing()
  return defaultPricing
}

export const createOrder = async (newOrder: TOrderForm) => {
  // Ensure default customer exists (for fallback pricing)
  await ensureDefaultCustomer()

  let actualCustomerId = newOrder.customerId
  let customerName = newOrder.customerId

  // ตรวจสอบว่าเป็นลูกค้าใหม่หรือไม่
  if (newOrder.customerId.startsWith('new_customer_')) {
    // สร้าง customer ID ใหม่และใช้ชื่อจาก temp ID
    customerName = newOrder.customerId.replace('new_customer_', '').replace(/_/g, ' ')
    // สร้าง ID ใหม่ด้วย nanoid
    const { generateCustomerId } = await import('@/utils/idGenerators')
    actualCustomerId = generateCustomerId()
  }

  // Check or create customer if it doesn't exist
  if (!(await checkOrCreateCustomer(actualCustomerId, customerName, 'GENERAL'))) {
    return
  }
  // Get customer pricing
  const pricing = await getCustomerPricing(actualCustomerId)

  if (!pricing) {
    return handleError(
      new Error('No pricing found for customer'),
      'Error: Customer pricing not found'
    )
  }

  // Debug: Log pricing to ensure all fields are present
  console.log('Customer pricing:', pricing)

  if (
    pricing.large_ice_cube_price_thb === null ||
    pricing.large_ice_cube_price_thb === undefined ||
    pricing.small_ice_cube_price_thb === null ||
    pricing.small_ice_cube_price_thb === undefined ||
    pricing.crushed_ice_price_thb === null ||
    pricing.crushed_ice_price_thb === undefined
  ) {
    return handleError(
      new Error('Incomplete pricing data for customer'),
      'Error: Customer pricing has null values'
    )
  }

  // Calculate order totals
  const orderAmounts = {
    largeIceCubeAmount: newOrder.largeIceCubeAmount || 0,
    smallIceCubeAmount: newOrder.smallIceCubeAmount || 0,
    crushedIceAmount: newOrder.crushedIceAmount || 0,
    coarselyCrushedIceAmount: newOrder.coarselyCrushedIceAmount || 0
  }

  const orderCalculation = calculateOrderTotal(orderAmounts, {
    largeIceCubePriceTHB: pricing.large_ice_cube_price_thb,
    smallIceCubePriceTHB: pricing.small_ice_cube_price_thb,
    crushedIcePriceTHB: pricing.crushed_ice_price_thb
  })

  const totalAmount = orderCalculation.totalAmount

  const createdAt = getCurrentTimestamp()
  const { data: newOrderBook, error: orderBookError } = await supabaseClient
    .from('OrderBook')
    .insert({
      ...(newOrder.largeIceCubeAmount && {
        large_ice_cube_amount: newOrder.largeIceCubeAmount
      }),
      ...(newOrder.smallIceCubeAmount && {
        small_ice_cube_amount: newOrder.smallIceCubeAmount
      }),
      ...(newOrder.crushedIceAmount && {
        crushed_ice_amount: newOrder.crushedIceAmount
      }),
      ...(newOrder.coarselyCrushedIceAmount && {
        coarsely_crushed_ice_amount: newOrder.coarselyCrushedIceAmount
      }),
      monitor_number: newOrder.monitorNumber,
      monitor_number_update_at: newOrder.monitorNumber > 0
        ? createdAt
        : null,
      customer_id: actualCustomerId,
      // Store pricing snapshot at time of order creation
      large_ice_cube_price_thb: pricing.large_ice_cube_price_thb,
      small_ice_cube_price_thb: pricing.small_ice_cube_price_thb,
      crushed_ice_price_thb: pricing.crushed_ice_price_thb,
      // Payment tracking
      total_amount_thb: totalAmount,
      payment_status: 'UNPAID',
      amount_paid_thb: 0,
      outstanding_balance_thb: 0,
      is_monitor_display: false,
      status: 'OPEN',
      created_at: createdAt,
      updated_at: createdAt
    })

  if (orderBookError) {
    return handleError(orderBookError, 'Error creating OrderBook:')
  }

  return newOrderBook
}

export async function updateOrder(
  orderBookId: number,
  updatedOrderData: TOrderForm
) {
  let actualCustomerId = updatedOrderData.customerId
  let customerName = updatedOrderData.customerId

  // ตรวจสอบว่าเป็นลูกค้าใหม่หรือไม่
  if (updatedOrderData.customerId.startsWith('new_customer_')) {
    // สร้าง customer ID ใหม่และใช้ชื่อจาก temp ID
    customerName = updatedOrderData.customerId.replace('new_customer_', '').replace(/_/g, ' ')
    // สร้าง ID ใหม่ด้วย nanoid
    const { generateCustomerId } = await import('@/utils/idGenerators')
    actualCustomerId = generateCustomerId()
  }

  // Check or create customer if it doesn't exist
  if (!(await checkOrCreateCustomer(actualCustomerId, customerName, 'GENERAL'))) {
    return
  }

  // Get customer pricing for recalculation
  const pricing = await getCustomerPricing(actualCustomerId)

  if (!pricing) {
    return handleError(
      new Error('No pricing found for customer'),
      'Error: Customer pricing not found'
    )
  }

  // Calculate new order totals
  // Normalize: empty string / null / undefined → 0 (user cleared the field)
  const toAmount = (v: number | null | undefined | string): number => {
    if (v === null || v === undefined || v === '') return 0
    const n = Number(v)
    return isNaN(n) ? 0 : n
  }

  const orderAmounts = {
    largeIceCubeAmount: toAmount(updatedOrderData.largeIceCubeAmount),
    smallIceCubeAmount: toAmount(updatedOrderData.smallIceCubeAmount),
    crushedIceAmount: toAmount(updatedOrderData.crushedIceAmount),
    coarselyCrushedIceAmount: toAmount(updatedOrderData.coarselyCrushedIceAmount)
  }

  const orderCalculation = calculateOrderTotal(orderAmounts, {
    largeIceCubePriceTHB: pricing.large_ice_cube_price_thb,
    smallIceCubePriceTHB: pricing.small_ice_cube_price_thb,
    crushedIcePriceTHB: pricing.crushed_ice_price_thb
  })

  const totalAmount = orderCalculation.totalAmount

  // Step 1: ดึง monitorNumber เดิมจาก database
  const { data: currentOrder, error: fetchError } = await supabaseClient
    .from('OrderBook')
    .select('monitor_number')
    .eq('id', orderBookId)
    .single()

  if (fetchError) {
    return handleError(fetchError, 'Error fetching current order for monitor number check:')
  }

  const oldMonitorNumber = currentOrder.monitor_number
  const newMonitorNumber = updatedOrderData.monitorNumber

  // Step 2: ตรวจสอบว่า monitorNumber เปลี่ยนหรือไม่
  const monitorNumberChanged = oldMonitorNumber !== newMonitorNumber
  const shouldSetMonitorUpdateAt = monitorNumberChanged && newMonitorNumber > 0

  const updatedAt = getCurrentTimestamp()
  const { data: updatedOrderBook, error: orderBookError } = await supabaseClient
    .from('OrderBook')
    .update({
      large_ice_cube_amount: orderAmounts.largeIceCubeAmount,
      small_ice_cube_amount: orderAmounts.smallIceCubeAmount,
      crushed_ice_amount: orderAmounts.crushedIceAmount,
      coarsely_crushed_ice_amount: orderAmounts.coarselyCrushedIceAmount,
      monitor_number: newMonitorNumber,
      customer_id: actualCustomerId,
      // Update pricing snapshot
      large_ice_cube_price_thb: pricing.large_ice_cube_price_thb,
      small_ice_cube_price_thb: pricing.small_ice_cube_price_thb,
      crushed_ice_price_thb: pricing.crushed_ice_price_thb,
      // Recalculate totals
      total_amount_thb: totalAmount,
      outstanding_balance_thb: totalAmount, // Reset outstanding balance on update
      is_monitor_display: false,
      updated_at: updatedAt,
      // Set monitor_number_update_at เฉพาะเมื่อ monitorNumber เปลี่ยนเป็นค่า > 0
      ...(shouldSetMonitorUpdateAt && {
        monitor_number_update_at: new Date().toISOString()
      })
    })
    .eq('id', orderBookId)

  if (orderBookError) {
    return handleError(orderBookError, 'Error updating OrderBook:')
  }

  return updatedOrderBook
}

export async function updateOrderComplete(orderBookId: number) {
  const completedAt = new Date()
  const { data: orderBookCompleted, error: orderBookError } =
    await supabaseClient
      .from('OrderBook')
      .update({
        status: 'COMPLETED',
        completed_at: completedAt,
        is_monitor_display: false
      })
      .eq('id', orderBookId)

  if (orderBookError) {
    console.error('Error update order:', orderBookError)
    return
  }

  return orderBookCompleted
}

export async function switchOrderMonitorDisplay(
  orderId: number,
  monitorNo: number,
  isMonitorDisplay: boolean
) {
  const updatedAt = new Date()
  const swapMonitorDisplayStatus = !isMonitorDisplay

  const { data: deletedAtOrderBook, error: transactionError } =
    await supabaseClient
      .from('OrderBook')
      .update({
        is_monitor_display: false,
        updated_at: updatedAt
      })
      .eq('is_monitor_display', true)
      .eq('monitor_number', monitorNo)
      .then(() =>
        supabaseClient
          .from('OrderBook')
          .update({
            is_monitor_display: swapMonitorDisplayStatus,
            updated_at: updatedAt,
            // Update when user click monitor display order
            ...(swapMonitorDisplayStatus && {
              monitor_display_at: updatedAt
            })
          })
          .eq('id', orderId)
      )

  if (transactionError) {
    return handleError(transactionError, 'Error switch order monitor display')
  }

  return deletedAtOrderBook
}

export async function softDeleteOrder(orderId: number) {
  const deletedAt = new Date()
  const { data: deletedAtOrderBook, error: orderBookError } =
    await supabaseClient
      .from('OrderBook')
      .update({
        status: 'DELETED',
        updated_at: deletedAt,
        deleted_at: deletedAt,
        is_monitor_display: false
      })
      .eq('id', orderId)

  if (orderBookError) {
    return handleError(orderBookError, 'Error deleting OrderBook:')
  }

  return deletedAtOrderBook
}

export async function getOrderBookStatusOpen() {
  const { data } = await supabaseClient
    .from('OrderBook')
    .select(
      `
      *,
      Customer!customer_id(*)
    `
    )
    .eq('status', 'OPEN')
    .order('created_at', { ascending: false } as any)

  return data?.map((d: any) => ({
    ...d,
    customer: d.Customer
  }))
}

export async function getQueueOrders() {
  const { data } = await supabaseClient
    .from('OrderBook')
    .select(
      `
      *,
      Customer!customer_id(*)
    `
    )
    .eq('status', 'OPEN')
    .eq('monitor_number', 0)
    .order('created_at', { ascending: true } as any)

  return (
    data?.map((d: any) => ({
      ...d,
      customer: d.Customer
    })) ?? []
  )
}

export async function findOrderBookFromMonitorNo(monitorNo: number) {
  const { data, error } = await supabaseClient
    .from('OrderBook')
    .select(
      `
      *,
      Customer!customer_id(*)
    `
    )
    .eq('monitor_number', monitorNo)
    .eq('status', 'OPEN')
    .is('is_monitor_display', true)
    .order('created_at', { ascending: false } as any)
    .limit(1)

  if (error) {
    console.error('Error fetching data', error)
    return []
  }

  return (
    data?.map((d: any) => ({
      ...d,
      customer: d.Customer
    })) ?? []
  )
}

export async function getOrderBookHistory(
  pageNumber: number,
  itemsPerPage: number,
  filters?: IGetOrderBookHistoryFilter
) {
  let query = supabaseClient
    .from('OrderBook')
    .select(
      `
      *,
      Customer!customer_id(*)
    `,
      { count: 'exact' }
    )
    .or('completed_at.not.is.null,deleted_at.not.is.null')
    .order('created_at', { ascending: false } as any)
    .range((pageNumber - 1) * itemsPerPage, pageNumber * itemsPerPage - 1)

  if (filters?.startDate && filters?.endDate) {
    query = query
      .gte('created_at', filters.startDate.toISOString())
      .lte('created_at', filters.endDate.toISOString())
  }

  if (filters?.customerId) {
    query = query.eq('customer_id', filters.customerId)
  }

  if (filters?.paymentStatus) {
    query = query.eq('payment_status', filters.paymentStatus)
  }

  if (filters?.status) {
    query = query.eq('status', filters.status)
  }

  const { data, count } = await query

  const totalItems = count ?? 0
  const totalPages = Math.ceil(totalItems / itemsPerPage)

  return {
    data:
      data?.map((d: any) => ({
        ...d,
        customer: d.Customer
      })) || [],
    totalPages,
    totalItems: count
  }
}

export async function assignOrderToMonitor(
  orderId: number,
  monitorNumber: number
) {
  if (monitorNumber !== 1 && monitorNumber !== 2) {
    return handleError(
      new Error('Invalid monitor number'),
      'Monitor number must be 1 or 2'
    )
  }

  const updatedAt = getCurrentTimestamp()
  const { data: updatedOrder, error: orderError } = await supabaseClient
    .from('OrderBook')
    .update({
      monitor_number: monitorNumber,
      monitor_number_update_at: new Date().toISOString(),
      is_monitor_display: false,
      updated_at: updatedAt
    })
    .eq('id', orderId)
    .eq('monitor_number', 0) // Only update if currently in queue
    .eq('status', 'OPEN') // Only update if order is still open

  if (orderError) {
    return handleError(orderError, 'Error assigning order to monitor:')
  }

  return updatedOrder
}

export async function getOrderBookHistoryExportData(
  filters?: IGetOrderBookHistoryFilter
) {
  let query = supabaseClient
    .from('OrderBook')
    .select(
      `
      *,
      Customer!customer_id(*)
    `
    )
    .or('completed_at.not.is.null,deleted_at.not.is.null')
    .order('created_at', { ascending: false } as any)

  if (filters?.startDate && filters?.endDate) {
    query = query
      .gte('created_at', filters.startDate.toISOString())
      .lte('created_at', filters.endDate.toISOString())
  }

  if (filters?.customerId) {
    query = query.eq('customer_id', filters.customerId)
  }

  if (filters?.paymentStatus) {
    query = query.eq('payment_status', filters.paymentStatus)
  }

  if (filters?.status) {
    query = query.eq('status', filters.status)
  }

  const { data } = await query

  return (
    data?.map((d: any) => ({
      ...d,
      customer: d.Customer
    })) ?? []
  )
}

// Payment Management Functions

export async function updateOrderPaymentStatus(
  orderBookId: number,
  paymentStatus: PaymentStatus,
  amountPaid?: number
) {
  const updatedAt = getCurrentTimestamp()

  // Get current order to calculate outstanding balance
  const { data: currentOrder, error: fetchError } = await supabaseClient
    .from('OrderBook')
    .select('total_amount_thb, amount_paid_thb')
    .eq('id', orderBookId)
    .single()

  if (fetchError) {
    return handleError(fetchError, 'Error fetching current order:')
  }

  const totalAmount = currentOrder.total_amount_thb
  const newAmountPaid =
    amountPaid !== undefined ? amountPaid : currentOrder.amount_paid_thb
  const outstandingBalance = Math.max(0, totalAmount - newAmountPaid)

  const { data: updatedOrder, error: updateError } = await supabaseClient
    .from('OrderBook')
    .update({
      payment_status: paymentStatus,
      ...(amountPaid !== undefined && { amount_paid_thb: amountPaid }),
      outstanding_balance_thb: outstandingBalance,
      updated_at: updatedAt
    })
    .eq('id', orderBookId)
    .select()
    .single()

  if (updateError) {
    return handleError(updateError, 'Error updating payment status:')
  }

  return updatedOrder
}

export async function recordPayment(
  orderBookId: number,
  paymentAmount: number,
  paymentMethod: 'CASH' | 'TRANSFER'
) {
  // Get current order details
  const { data: currentOrder, error: fetchError } = await supabaseClient
    .from('OrderBook')
    .select('total_amount_thb, amount_paid_thb, outstanding_balance_thb')
    .eq('id', orderBookId)
    .single()

  if (fetchError) {
    return handleError(fetchError, 'Error fetching current order:')
  }

  const newAmountPaid = currentOrder.amount_paid_thb + paymentAmount
  const newOutstandingBalance = Math.max(
    0,
    currentOrder.total_amount_thb - newAmountPaid
  )

  // Determine payment status based on outstanding balance
  const paymentStatus: PaymentStatus =
    newOutstandingBalance === 0
      ? (paymentMethod as PaymentStatus)
      : PaymentStatus.UNPAID

  return await updateOrderPaymentStatus(
    orderBookId,
    paymentStatus,
    newAmountPaid
  )
}

export async function getOrdersByPaymentStatus(paymentStatus: PaymentStatus) {
  const { data, error } = await supabaseClient
    .from('OrderBook')
    .select(
      `
      *,
      Customer!customer_id(*)
    `
    )
    .eq('payment_status', paymentStatus)
    .order('created_at', { ascending: false })

  if (error) {
    return handleError(error, 'Error fetching orders by payment status:')
  }

  return (
    data?.map((d: any) => ({
      ...d,
      customer: d.Customer
    })) ?? []
  )
}

export async function getOutstandingOrders() {
  const { data, error } = await supabaseClient
    .from('OrderBook')
    .select(
      `
      *,
      Customer!customer_id(*)
    `
    )
    .gt('outstanding_balance_thb', 0)
    .order('created_at', { ascending: false })

  if (error) {
    return handleError(error, 'Error fetching outstanding orders:')
  }

  return (
    data?.map((d: any) => ({
      ...d,
      customer: d.Customer
    })) ?? []
  )
}

export async function getPaymentSummary(startDate?: Date, endDate?: Date) {
  let query = supabaseClient
    .from('OrderBook')
    .select(
      'total_amount_thb, amount_paid_thb, outstanding_balance_thb, payment_status, created_at'
    )

  if (startDate && endDate) {
    query = query
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString())
  }

  const { data, error } = await query

  if (error) {
    return handleError(error, 'Error fetching payment summary:')
  }

  const summary = {
    totalOrders: data?.length || 0,
    totalAmount:
      data?.reduce((sum, order) => sum + order.total_amount_thb, 0) || 0,
    totalPaid:
      data?.reduce((sum, order) => sum + order.amount_paid_thb, 0) || 0,
    totalOutstanding:
      data?.reduce((sum, order) => sum + order.outstanding_balance_thb, 0) || 0,
    paidOrders:
      data?.filter(order => order.payment_status !== 'UNPAID').length || 0,
    unpaidOrders:
      data?.filter(order => order.payment_status === 'UNPAID').length || 0,
    cashPayments:
      data?.filter(order => order.payment_status === 'CASH').length || 0,
    transferPayments:
      data?.filter(order => order.payment_status === 'TRANSFER').length || 0
  }

  return summary
}

// Void Order Management

export async function toggleVoidOrder(
  orderId: number,
  isVoided: boolean
): Promise<boolean> {
  const updatedAt = getCurrentTimestamp()

  const { error } = await supabaseClient
    .from('OrderBook')
    .update({
      is_voided_from_sales: isVoided,
      updated_at: updatedAt
    })
    .eq('id', orderId)

  if (error) {
    console.error('Error toggling void order:', error)
    return false
  }

  return true
}
